import { useEffect, useState, type ElementType } from 'react';
import { Ellipsis, Plus } from 'lucide-react';
import { cn } from '../../lib/cn';
import { CountBadge } from '../ui/count-badge';
import { Icon } from '../ui/icon';
import type { NavItem, NavSection } from './sidebar';

/** Eingabetypen, bei denen am Handy die Bildschirmtastatur aufgeht. */
const OHNE_TASTATUR = new Set(['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file', 'image', 'hidden']);

function tastaturFeld(el: Element | null) {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') return true;
  return el.tagName === 'INPUT' && !OHNE_TASTATUR.has((el as HTMLInputElement).type);
}

function touchGeraet() {
  return window.matchMedia('(pointer: coarse)').matches || document.documentElement.dataset.pointer === 'coarse';
}

/**
 * `true`, solange auf einem Touch-Gerät ein Eingabefeld den Fokus hat, also
 * die Bildschirmtastatur offen ist. Die Leiste stünde dann auf der Tastatur
 * (iOS) oder nähme ihr Platz weg (Android) und blendet sich aus.
 */
function useTastaturOffen() {
  const [offen, setOffen] = useState(false);
  useEffect(() => {
    let zeitgeber = 0;
    const pruefen = () => setOffen(touchGeraet() && tastaturFeld(document.activeElement));
    // Beim Wechsel von Feld zu Feld steht activeElement während focusout kurz
    // auf <body>; erst danach prüfen, sonst blinkt die Leiste.
    const spaeter = () => {
      window.clearTimeout(zeitgeber);
      zeitgeber = window.setTimeout(pruefen, 0);
    };
    document.addEventListener('focusin', pruefen);
    document.addEventListener('focusout', spaeter);
    return () => {
      window.clearTimeout(zeitgeber);
      document.removeEventListener('focusin', pruefen);
      document.removeEventListener('focusout', spaeter);
    };
  }, []);
  return offen;
}

const tabKlasse =
  'group flex h-full w-full min-w-0 cursor-pointer flex-col items-center justify-center gap-0.5 text-muted-foreground no-underline transition-colors duration-[120ms] aria-[current=page]:text-foreground';
const pilleKlasse =
  'flex h-8 w-14 items-center justify-center rounded-full transition-colors duration-[120ms] group-hover:bg-sidebar-accent group-aria-[current=page]:bg-highlight-soft group-aria-[current=page]:text-highlight';

interface BottomNavProps {
  tabs: NavItem[];
  activeId?: string;
  /** Das Mehr-Sheet ist offen oder das aktive Ziel ist kein Tab. */
  mehrAktiv: boolean;
  onMehr: () => void;
  /** Summe der Zahlen im Fuß von „Mehr“ (ungelesene Benachrichtigungen): als Zahl am Tab. */
  mehrCount?: number;
  onSelect: (id: string) => void;
  linkAs: ElementType;
  schnellErfassen?: { label: string; onClick: () => void };
}

/**
 * Tab-Leiste unten unter 768 px (M1): Start-Tab links, „+“ als runde
 * Hauptaktion in der Mitte, „Mehr“ rechts. Wird von der AppShell gerendert.
 */
export function BottomNav({ tabs, activeId, mehrAktiv, onMehr, mehrCount, onSelect, linkAs: LinkAs, schnellErfassen }: BottomNavProps) {
  const tastatur = useTastaturOffen();

  const tab = (it: NavItem) => {
    const Comp: ElementType = it.href ? LinkAs : 'button';
    return (
      <li key={it.id} className="flex min-w-0 flex-1">
        <Comp
          {...(it.href ? { href: it.href } : { type: 'button' })}
          aria-current={activeId === it.id && !mehrAktiv ? 'page' : undefined}
          onClick={() => onSelect(it.id)}
          className={tabKlasse}
        >
          <span className={pilleKlasse}>{it.icon && <Icon icon={it.icon} size={22} />}</span>
          <span className="max-w-full truncate px-1 t-label">{it.label}</span>
        </Comp>
      </li>
    );
  };

  const plaetze = tabs.map(tab);
  plaetze.push(
    <li key="mehr" className="flex min-w-0 flex-1">
      <button
        type="button"
        aria-current={mehrAktiv ? 'page' : undefined}
        aria-haspopup="dialog"
        aria-label={mehrCount ? `Mehr, ${mehrCount} ungelesene Benachrichtigungen` : undefined}
        onClick={onMehr}
        className={tabKlasse}
      >
        <span className={cn(pilleKlasse, 'relative')}>
          <Icon icon={Ellipsis} size={22} />
          {mehrCount != null && mehrCount > 0 && (
            <CountBadge tone="primary" className="absolute -right-1 -top-1.5">
              {mehrCount}
            </CountBadge>
          )}
        </span>
        <span className="t-label">Mehr</span>
      </button>
    </li>,
  );
  if (schnellErfassen) {
    // „+“ in der Mitte der Plätze: CRM Übersicht · Kunden · + · Aufgaben · Mehr, PM Meine Arbeit · + · Mehr.
    plaetze.splice(
      Math.floor(plaetze.length / 2),
      0,
      <li key="schnell-erfassen" className="flex min-w-0 flex-1 items-center justify-center">
        <button
          type="button"
          aria-label={schnellErfassen.label}
          aria-haspopup="dialog"
          onClick={schnellErfassen.onClick}
          className="flex size-12 cursor-pointer items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity duration-150 hover:opacity-88"
        >
          <Icon icon={Plus} size={24} />
        </button>
      </li>,
    );
  }

  return (
    <nav
      aria-label="Hauptnavigation"
      data-ek-bottom-nav=""
      hidden={tastatur}
      className="fixed inset-x-0 bottom-0 z-40 border-t border-sidebar-border bg-sidebar pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] md:hidden"
    >
      <ul className="mx-auto flex h-15 max-w-lg items-stretch">{plaetze}</ul>
    </nav>
  );
}

interface MehrListeProps {
  sections: NavSection[];
  footer: NavItem[];
  activeId?: string;
  onSelect: (id: string) => void;
  linkAs: ElementType;
}

/** Inhalt von „Mehr“: alle Ziele der Sidebar, die keine Tabs sind, und ihr Fuß. Optik wie die Sidebar, Zeilen 48 px. */
export function MehrListe({ sections, footer, activeId, onSelect, linkAs: LinkAs }: MehrListeProps) {
  const eintrag = (it: NavItem) => {
    const Comp: ElementType = it.href ? LinkAs : 'button';
    return (
      <li key={it.id}>
        <Comp
          {...(it.href ? { href: it.href } : { type: 'button' })}
          aria-current={activeId === it.id ? 'page' : undefined}
          onClick={() => onSelect(it.id)}
          className={cn(
            'group flex h-12 w-full cursor-pointer items-center gap-3 rounded-md px-3 text-left t-body text-foreground no-underline transition-colors duration-[120ms]',
            'hover:bg-accent aria-[current=page]:bg-sidebar-primary aria-[current=page]:font-medium aria-[current=page]:text-sidebar-primary-foreground',
          )}
        >
          {it.icon && <Icon icon={it.icon} size={20} className="text-muted-foreground group-aria-[current=page]:text-inherit" />}
          <span className="min-w-0 flex-1 truncate">{it.label}</span>
          {it.count != null && <CountBadge cap={false} className="group-aria-[current=page]:bg-transparent group-aria-[current=page]:text-inherit">{it.count}</CountBadge>}
        </Comp>
      </li>
    );
  };
  return (
    <nav aria-label="Weitere Ziele" className="-mx-2 flex flex-col gap-4">
      {sections.map((s, i) => (
        <div key={s.label ?? i}>
          {s.label && <div className="px-3 pb-1 t-label text-muted-foreground">{s.label}</div>}
          <ul className="flex flex-col gap-0.5">{s.items.map(eintrag)}</ul>
        </div>
      ))}
      {footer.length > 0 && <ul className="flex flex-col gap-0.5 border-t border-border pt-3">{footer.map(eintrag)}</ul>}
    </nav>
  );
}
